import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { colors, radius, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const DEFAULT_CONFIG = {
  is_active: false,
  auto_schedule: false,
  ty_merchant_id: '',
  only_seller_reviews: true,
  min_rate: 1,
  max_reviews: 30,
  show_media: true,
  mask_names: true,
  show_rating_bars: true,
  title: 'Müşteri Yorumları',
  attribution_text: 'Trendyol mağazamızdaki doğrulanmış alışveriş yorumları',
  anchor_selector: '',
  position: 'after',
  test_product_ids: '',
  require_approval: false,
  match_barcode: false,
}

const DURUM = {
  auto: { label: 'Otomatik', style: ui.chip },
  manual: { label: 'Manuel', style: ui.chip },
  pending: { label: 'Onay bekliyor', style: { ...ui.chip, background: colors.warningLight, color: colors.warningText } },
  unmatched: { label: 'Eşleşmedi', style: ui.chip },
  excluded: { label: 'Hariç', style: { ...ui.chip, background: colors.dangerLight, color: colors.dangerText } },
}

function Row({ label, hint, children }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={s.labelNormal}>{label}</label>
      {children}
      {hint && <p className="sx-break" style={{ ...s.sectionSubtitle, margin: '5px 0 0' }}>{hint}</p>}
    </div>
  )
}

function Toggle({ checked, onChange, label, hint }) {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px' }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}
        style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: colors.primary, cursor: 'pointer' }} />
      <div>
        <div style={{ ...s.labelNormal, marginBottom: 0 }}>{label}</div>
        {hint && <div style={{ ...s.sectionSubtitle, marginTop: '2px' }}>{hint}</div>}
      </div>
    </div>
  )
}

export default function SmartReview() {
  const { selectedBrand } = useBrand()
  const [tab, setTab] = useState('ayarlar')
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncMsg, setSyncMsg] = useState('')

  const [ozet, setOzet] = useState(null)
  const [products, setProducts] = useState([])
  const [durum, setDurum] = useState('all')
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)

  const [ingestKey, setIngestKey] = useState('')
  const [keyCopied, setKeyCopied] = useState(false)

  const [modOzet, setModOzet] = useState(null)
  const [modList, setModList] = useState([])
  const [modToplam, setModToplam] = useState(0)
  const [modDurum, setModDurum] = useState('bekleyen')
  const [modSeller, setModSeller] = useState('')
  const [modRate, setModRate] = useState(0)
  const [modMedya, setModMedya] = useState('')
  const [modQ, setModQ] = useState('')
  const [modUrun, setModUrun] = useState('')
  const [modSecili, setModSecili] = useState([])
  const [modLoading, setModLoading] = useState(false)
  const [modBusy, setModBusy] = useState(false)
  const [sellers, setSellers] = useState([])

  const [matchFor, setMatchFor] = useState(null)
  const [matchInput, setMatchInput] = useState('')
  const [preview, setPreview] = useState(null)
  const [matchBusy, setMatchBusy] = useState(false)
  const [matchErr, setMatchErr] = useState('')

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  useEffect(() => {
    if (!selectedBrand) return
    axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-review-config`, { headers })
      .then(res => {
        if (res.data && res.data.brand_id) {
          setConfig({ ...DEFAULT_CONFIG, ...res.data, ty_merchant_id: res.data.ty_merchant_id || '' })
          setModDurum(res.data.require_approval ? 'bekleyen' : 'yayinda')
        }
      })
      .catch(() => {})
  }, [selectedBrand?.id]) // eslint-disable-line

  const loadProducts = useCallback(() => {
    if (!selectedBrand) return
    setLoading(true)
    axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-review/products`, { headers, params: { durum, q } })
      .then(res => { setOzet(res.data.ozet); setProducts(res.data.products || []) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [selectedBrand?.id, durum, q]) // eslint-disable-line

  useEffect(() => { if (tab === 'eslestirme') loadProducts() }, [tab, loadProducts])

  const loadModeration = useCallback(() => {
    if (!selectedBrand) return
    setModLoading(true)
    setModSecili([])
    axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-review/moderation`, {
      headers,
      params: { durum: modDurum, seller: modSeller, rate: modRate, medya: modMedya, q: modQ, urun: modUrun, limit: 100 }
    })
      .then(res => { setModOzet(res.data.ozet); setModList(res.data.reviews || []); setModToplam(res.data.toplam || 0) })
      .catch(() => {})
      .finally(() => setModLoading(false))
  }, [selectedBrand?.id, modDurum, modSeller, modRate, modMedya, modQ, modUrun]) // eslint-disable-line

  useEffect(() => {
    if (tab !== 'moderasyon' || !selectedBrand) return
    loadModeration()
    axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-review/sellers`, { headers })
      .then(res => setSellers(res.data.sellers || []))
      .catch(() => {})
  }, [tab, loadModeration]) // eslint-disable-line

  function yayinDurumu(ids, yayinla) {
    if (!ids.length) return
    setModBusy(true)
    axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-review/publish`, { review_ids: ids, yayinla }, { headers })
      .then(() => axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-review/rebuild-stats`, {}, { headers }))
      .then(() => loadModeration())
      .catch(e => alert(e.response?.data?.error || e.message))
      .finally(() => setModBusy(false))
  }

  function filtreyeUyanlariYayinla(yayinla) {
    const adet = modToplam
    if (!window.confirm(`Filtreye uyan ${adet} yorumun tamamı ${yayinla ? 'yayınlanacak' : 'yayından kaldırılacak'}. Onaylıyor musunuz?`)) return
    setModBusy(true)
    axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-review/publish`,
      { hepsi: true, yayinla, filtre: { durum: modDurum, seller: modSeller, rate: modRate, medya: modMedya, q: modQ, urun: modUrun } }, { headers })
      .then(() => axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-review/rebuild-stats`, {}, { headers }))
      .then(() => loadModeration())
      .catch(e => alert(e.response?.data?.error || e.message))
      .finally(() => setModBusy(false))
  }

  function secimDegistir(id) {
    setModSecili(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])
  }

  function set(key, value) { setConfig(c => ({ ...c, [key]: value })) }

  function save() {
    if (!selectedBrand) return
    setSaving(true); setSaved(false)
    axios.put(`${API}/admin/brands/${selectedBrand.id}/smart-review-config`, config, { headers })
      .then(() => { setSaved(true); setTimeout(() => setSaved(false), 2500) })
      .catch(e => alert('Kaydedilemedi: ' + (e.response?.data?.error || e.message)))
      .finally(() => setSaving(false))
  }

  function loadKey() {
    axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-review/ingest-key`, { headers })
      .then(res => setIngestKey(res.data.key || ''))
      .catch(e => alert(e.response?.data?.error || e.message))
  }
  function resetKey() {
    if (!window.confirm('Anahtar yenilensin mi? Eski anahtarla çalışan toplayıcılar durur.')) return
    axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-review/ingest-key/reset`, {}, { headers })
      .then(res => setIngestKey(res.data.key || ''))
      .catch(e => alert(e.response?.data?.error || e.message))
  }

  function sync(full) {
    if (!selectedBrand) return
    if (!config.ty_merchant_id) { alert('Önce Trendyol mağaza kimliğini girip kaydedin'); return }
    setSyncing(true); setSyncMsg('')
    axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-review/sync`, { full: !!full }, { headers, timeout: 900000 })
      .then(res => { setSyncMsg(res.data.summary || 'Tamamlandı'); if (tab === 'eslestirme') loadProducts() })
      .catch(e => setSyncMsg('Hata: ' + (e.response?.data?.error || e.message)))
      .finally(() => setSyncing(false))
  }

  function doPreview() {
    setMatchErr(''); setPreview(null); setMatchBusy(true)
    axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-review/match`, { input: matchInput, preview: true }, { headers })
      .then(res => setPreview(res.data.preview))
      .catch(e => setMatchErr(e.response?.data?.error || e.message))
      .finally(() => setMatchBusy(false))
  }

  function confirmMatch() {
    setMatchBusy(true)
    axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-review/match`,
      { input: matchInput, product_id: matchFor.external_id }, { headers })
      .then(() => { setMatchFor(null); setMatchInput(''); setPreview(null); loadProducts() })
      .catch(e => setMatchErr(e.response?.data?.error || e.message))
      .finally(() => setMatchBusy(false))
  }

  function updateMatch(mapId, patch) {
    axios.put(`${API}/admin/brands/${selectedBrand.id}/smart-review/match/${mapId}`, patch, { headers })
      .then(loadProducts).catch(e => alert(e.response?.data?.error || e.message))
  }
  function deleteMatch(mapId) {
    if (!window.confirm('Bu eşleşme silinsin mi? Yorumlar veritabanında kalır.')) return
    axios.delete(`${API}/admin/brands/${selectedBrand.id}/smart-review/match/${mapId}`, { headers })
      .then(loadProducts).catch(e => alert(e.response?.data?.error || e.message))
  }

  if (!selectedBrand) return <div style={ui.notice}>Önce bir marka seçin.</div>

  // Onay modu: açıkken önyüzde is_published=true görünür; kapalıyken yorumlar
  // varsayılan görünür, is_hidden=true olanlar gizli. "canlı" = önyüzde görünüyor.
  const onayModu = config.require_approval === true
  const canli = r => onayModu ? !!r.is_published : !r.is_hidden

  return (
    <div>
      <div className="sx-wrap" style={{ ...s.flexBetween, flexWrap: 'wrap', gap: '10px', marginBottom: '18px' }}>
        <div>
          <h1 style={s.pageTitle}>Smart Review</h1>
          <p style={s.pageSubtitle}>Trendyol mağaza yorumlarını ürün detay sayfasında gösterir</p>
        </div>
        {(
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button style={s.btnSecondary} onClick={() => sync(false)} disabled={syncing}>
              {syncing ? 'Senkronize ediliyor…' : 'Şimdi senkronla'}
            </button>
            <button style={s.btnPrimary} onClick={save} disabled={saving}>
              {saving ? 'Kaydediliyor…' : saved ? 'Kaydedildi' : 'Kaydet'}
            </button>
          </div>
        )}
      </div>

      {syncMsg && (
        <div style={{ ...(syncMsg.startsWith('Hata') ? s.alertDanger : ui.notice), marginBottom: '16px' }}>{syncMsg}</div>
      )}
      {config.schema_ok === false && (
        <div style={{ ...s.alertWarning, marginBottom: '16px' }}>
          Trendyol veri şeması değişmiş görünüyor (kanarya kontrolü başarısız). Önyüzde yorum bloğu gizleniyor,
          veritabanındaki yorumlar korunuyor.
        </div>
      )}
      {config.last_error && (
        <div style={{ ...s.alertDanger, marginBottom: '16px' }}>Son senkron hatası: {config.last_error}</div>
      )}

      <div style={ui.tabBar}>
        {[['ayarlar', 'Ayarlar'], ['eslestirme', 'Ürün Eşleştirme'], ['moderasyon', 'Yorum Onayı']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={ui.tab(tab === k)}>{l}</button>
        ))}
      </div>

      {tab === 'ayarlar' && (
        <>
          <div style={s.card}>
            <h3 style={s.sectionTitle}>Trendyol bağlantısı</h3>
            <p style={{ ...s.sectionSubtitle, marginBottom: '16px' }}>
              Mağaza kimliği, Trendyol mağaza sayfanızın adresindeki <code>merchantId</code> değeridir.
            </p>
            <Row label="Trendyol mağaza kimliği (merchantId)"
              hint="Örnek: 590921. Ürün listesi ve yorumlar bu mağazaya göre çekilir.">
              <input style={{ ...s.input, maxWidth: '260px' }} value={config.ty_merchant_id}
                onChange={e => set('ty_merchant_id', e.target.value.replace(/\D/g, ''))} placeholder="590921" />
            </Row>
            <Toggle checked={config.only_seller_reviews} onChange={v => set('only_seller_reviews', v)}
              label="Sadece kendi mağazamın yorumları"
              hint="Kapatırsanız aynı ürünü satan diğer satıcıların yorumları da çekilir. Yayıncı markalarda kapatmak gerekebilir." />
            <Toggle checked={config.require_approval} onChange={v => set('require_approval', v)}
              label="Yorumlar onaydan geçsin"
              hint="Açıkken çekilen yorumlar önyüzde otomatik görünmez; Yorum Onayı sekmesinden seçip yayınladıklarınız görünür. Kapalıyken tüm yorumlar doğrudan yayınlanır." />
            <Toggle checked={config.match_barcode} onChange={v => set('match_barcode', v)}
              label="Barkod ile eşleştir"
              hint="Açıkken Trendyol ürününün barkodu, mağazanızdaki ürünün barkoduyla eşleştirilir; barkod tutuyorsa ürün kodu/isim benzerliği aranmaz. Barkodları düzgün girilmiş kataloglarda eşleşen ürün sayısını artırır." />
            <Toggle checked={config.auto_schedule} onChange={v => set('auto_schedule', v)}
              label="Otomatik senkron (her gece 04:00)"
              hint="Kapalıyken yorumlar yalnızca 'Şimdi senkronla' ile güncellenir." />
            <div style={{ display: 'flex', gap: '10px', marginTop: '6px', flexWrap: 'wrap' }}>
              <button style={s.btnSecondary} onClick={() => sync(true)} disabled={syncing}>
                Tam tarama (ürün kodlarını yeniden çöz)
              </button>
            </div>
            {config.last_run_at && (
              <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '12px 0 0' }}>
                Son senkron: {new Date(config.last_run_at).toLocaleString('tr-TR')} — {config.last_run_count || 0} yorum yazıldı
              </p>
            )}
          </div>

          <div style={s.card}>
            <h3 style={s.sectionTitle}>Toplayıcı</h3>
            <p style={{ ...s.sectionSubtitle, marginBottom: '14px' }}>
              Trendyol, sunucu IP'lerinden gelen istekleri engelliyor. Bu yüzden yorumlar ofis bağlantısı üzerinden
              çalışan bir toplayıcı ile çekilip panele gönderilir. Aşağıdaki anahtar toplayıcının kimliğidir.
            </p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
              <input readOnly value={ingestKey || '••••••••'} style={{ ...s.input, maxWidth: '420px', fontFamily: 'monospace', fontSize: '12px' }} />
              {!ingestKey
                ? <button style={s.btnSecondary} onClick={loadKey}>Anahtarı göster</button>
                : <>
                    <button style={s.btnSecondary} onClick={() => { navigator.clipboard.writeText(ingestKey); setKeyCopied(true); setTimeout(() => setKeyCopied(false), 2000) }}>
                      {keyCopied ? 'Kopyalandı' : 'Kopyala'}
                    </button>
                    <button style={ui.btnDangerQuiet} onClick={resetKey}>Yenile</button>
                  </>}
            </div>
            {config.last_ingest_at && (
              <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '0 0 14px' }}>
                Toplayıcıdan son veri: {new Date(config.last_ingest_at).toLocaleString('tr-TR')}
              </p>
            )}
            <div style={s.cardFlat}>
              <div style={{ ...s.labelNormal, marginBottom: '8px' }}>Kurulum</div>
              <p style={{ ...s.sectionSubtitle, margin: '0 0 8px' }}>
                Ofis bilgisayarında bir kez çalıştırın, sonra Görev Zamanlayıcı ile her gün tekrarlayın:
              </p>
              <pre className="sx-scroll-x" style={{
                background: colors.bgCard, color: colors.textPrimary, border: `1px solid ${colors.border}`,
                padding: '12px 14px', borderRadius: radius.md, maxWidth: '100%',
                fontSize: '12px', overflowX: 'auto', margin: 0, lineHeight: 1.6,
              }}>{`set SEUX_KEY=${ingestKey || '<anahtar>'}
node "C:\\Users\\Said\\source\\Seux Smart\\backend\\collector\\smart-review-collector.js"`}</pre>
            </div>
          </div>

          <div style={s.card}>
            <h3 style={s.sectionTitle}>Gösterim</h3>
            <p style={{ ...s.sectionSubtitle, marginBottom: '16px' }}>Ürün detay sayfasında yorum bloğunun görünümü.</p>
            <Toggle checked={config.is_active} onChange={v => set('is_active', v)}
              label="Modül aktif"
              hint="Kapalıyken önyüzde hiçbir şey basılmaz, senkron çalışmaya devam eder." />
            <Row label="Sadece bu ürünlerde göster (test)"
              hint="Virgülle ayrılmış ürün ID'leri. Doluyken yorumlar YALNIZCA bu ürünlerde görünür — firmaya inceletmek için kullanılır. Boş bırakınca tüm eşleşen ürünlerde açılır.">
              <input style={s.input} value={config.test_product_ids || ''}
                onChange={e => set('test_product_ids', e.target.value)} placeholder="örn. 557, 130, 172" />
            </Row>
            <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '14px' }}>
              <Row label="Başlık">
                <input style={s.input} value={config.title} onChange={e => set('title', e.target.value)} />
              </Row>
              <Row label="Kaynak yazısı" hint="Blok başlığının yanında küçük punto ile görünür.">
                <input style={s.input} value={config.attribution_text} onChange={e => set('attribution_text', e.target.value)} />
              </Row>
              <Row label="En düşük puan" hint="Bu puanın altındaki yorumlar gösterilmez.">
                <select style={s.select} value={config.min_rate} onChange={e => set('min_rate', Number(e.target.value))}>
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} yıldız ve üzeri</option>)}
                </select>
              </Row>
              <Row label="Ürün başına gösterilecek yorum">
                <input type="number" min="1" max="200" style={s.input} value={config.max_reviews}
                  onChange={e => set('max_reviews', Number(e.target.value))} />
              </Row>
            </div>
            <Toggle checked={config.mask_names} onChange={v => set('mask_names', v)}
              label="Müşteri adlarını maskele"
              hint="Ad açık, soyadı baş harfe indirgenir. Trendyol tarafında tamamen gizlenmiş isimler 'Trendyol Müşterisi' olarak görünür." />
            <Toggle checked={config.show_media} onChange={v => set('show_media', v)}
              label="Yorum fotoğraflarını göster"
              hint="Görseller Trendyol CDN'inden gösterilir, sunucumuza indirilmez." />
            <Toggle checked={config.show_rating_bars} onChange={v => set('show_rating_bars', v)}
              label="Puan dağılım çubuklarını göster" />
            <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginTop: '6px' }}>
              <Row label="Yerleşim seçicisi (CSS)" hint="Yorum bloğunun ekleneceği elemanın seçicisi. Virgülle birden fazla yazılabilir.">
                <input style={s.input} value={config.anchor_selector} onChange={e => set('anchor_selector', e.target.value)}
                  placeholder=".product-detail-tabs" />
              </Row>
              <Row label="Konum">
                <select style={s.select} value={config.position} onChange={e => set('position', e.target.value)}>
                  <option value="after">Seçilen elemanın altına</option>
                  <option value="before">Seçilen elemanın üstüne</option>
                </select>
              </Row>
            </div>
          </div>
        </>
      )}

      {tab === 'eslestirme' && (
        <>
          {ozet && (
            <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '12px', marginBottom: '16px' }}>
              {[
                ['Toplam ürün', ozet.toplam], ['Otomatik eşleşen', ozet.auto], ['Manuel', ozet.manual],
                ['Onay bekleyen', ozet.pending], ['Eşleşmeyen', ozet.unmatched], ['Yorumu olan', ozet.yorumlu],
              ].map(([l, v]) => (
                <div key={l} style={ui.statBox}>
                  <div style={{ ...ui.statValue, margin: '0 0 2px' }}>{v}</div>
                  <div style={ui.statLabel}>{l}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ ...s.card, paddingBottom: '8px' }}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <select style={{ ...s.select, width: 'auto' }} value={durum} onChange={e => setDurum(e.target.value)}>
                <option value="all">Tüm ürünler</option>
                <option value="auto">Otomatik eşleşenler</option>
                <option value="pending">Onay bekleyenler</option>
                <option value="manual">Manuel eşleşenler</option>
                <option value="unmatched">Eşleşmeyenler</option>
              </select>
              <input style={{ ...s.input, maxWidth: '280px' }} placeholder="Ürün adı veya kodu ara"
                value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadProducts()} />
              <button style={s.btnSecondary} onClick={loadProducts} disabled={loading}>
                {loading ? 'Yükleniyor…' : 'Listele'}
              </button>
            </div>

            <div className="sx-scroll-x" style={{ overflowX: 'auto' }}>
              <table className="sx-cards" style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Ürün</th>
                    <th style={s.th}>Kod</th>
                    <th style={s.th}>Durum</th>
                    <th style={s.th}>Trendyol eşleşmesi</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Yorum</th>
                    <th style={s.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.external_id}>
                      <td data-label="Ürün" style={{ ...s.td, maxWidth: '280px' }}>{p.name}</td>
                      <td data-label="Kod" style={{ ...s.td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{p.product_code || '—'}</td>
                      <td data-label="Durum" style={s.td}>
                        <span style={(DURUM[p.status] || DURUM.unmatched).style}>{(DURUM[p.status] || DURUM.unmatched).label}</span>
                      </td>
                      <td data-label="Trendyol eşleşmesi" style={{ ...s.td, minWidth: '260px' }}>
                        {(p.matches || []).length === 0 && <span style={{ color: colors.textMuted }}>—</span>}
                        {(p.matches || []).map(m => (
                          <div key={m.map_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <a href={m.ty_url || `https://www.trendyol.com/x-p-${m.content_id}`} target="_blank" rel="noreferrer"
                              style={{ color: colors.primary, fontSize: '12px', textDecoration: 'none', maxWidth: '190px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.ty_name || m.content_id}
                            </a>
                            <span style={{ ...ui.chip, padding: '1px 7px', fontSize: '11px' }}>
                              {m.manual ? 'manuel' : m.method}{!m.manual && m.method === 'name' ? ` %${Math.round(m.score * 100)}` : ''}
                            </span>
                            {!m.approved && (
                              <button style={{ ...ui.btnSm, padding: '2px 8px', fontSize: '11px' }}
                                onClick={() => updateMatch(m.map_id, { is_approved: true })}>Onayla</button>
                            )}
                            <button style={{ ...ui.btnDangerQuiet, padding: '2px 8px', fontSize: '11px' }}
                              onClick={() => deleteMatch(m.map_id)}>Kaldır</button>
                          </div>
                        ))}
                      </td>
                      <td data-label="Yorum" style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(p.comment_count) > 0
                          ? <span><b style={ui.num}>{p.comment_count}</b> <span style={{ ...ui.numMuted, fontSize: '12px' }}>({Number(p.avg_rating).toFixed(1)})</span></span>
                          : <span style={{ ...ui.numMuted, color: colors.textMuted }}>0</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button style={ui.btnSm} onClick={() => { setMatchFor(p); setMatchInput(''); setPreview(null); setMatchErr('') }}>
                          Trendyol linki bağla
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!products.length && !loading && (
                    <tr><td style={{ ...s.td, color: colors.textMuted }} colSpan={6}>Kayıt bulunamadı.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'moderasyon' && (
        <>
          {!onayModu && (
            <div style={{ ...ui.notice, marginBottom: '16px' }}>
              Onay modu kapalı — yorumlar önyüzde varsayılan olarak görünür. Buradan tek tek (veya toplu)
              istediğiniz yorumu <b>yayından kaldırabilir</b>, kaldırdığınızı geri yayınlayabilirsiniz.
              Yeni gelen yorumların önce onaydan geçmesini isterseniz Ayarlar’dan “Yorumlar onaydan geçsin”i açın.
            </div>
          )}

          {modOzet && (
            <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '12px', marginBottom: '16px' }}>
              {(onayModu
                ? [['Toplam yorum', modOzet.toplam], ['Onay bekleyen', modOzet.bekleyen], ['Yayında', modOzet.yayinda], ['Gizlenen', modOzet.gizli]]
                : [['Toplam yorum', modOzet.toplam], ['Yayında', modOzet.yayinda], ['Yayından kaldırılan', modOzet.gizli]]
              ).map(([l, v]) => (
                <div key={l} style={ui.statBox}>
                  <div style={{ ...ui.statValue, margin: '0 0 2px' }}>{v}</div>
                  <div style={ui.statLabel}>{l}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ ...s.card, paddingBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
              <select style={{ ...s.select, width: 'auto' }} value={modDurum} onChange={e => setModDurum(e.target.value)}>
                {onayModu && <option value="bekleyen">Onay bekleyenler</option>}
                <option value="yayinda">Yayındakiler</option>
                <option value="gizli">{onayModu ? 'Gizlenenler' : 'Yayından kaldırılanlar'}</option>
                <option value="hepsi">Tümü</option>
              </select>
              <select style={{ ...s.select, width: 'auto', maxWidth: '240px' }} value={modSeller} onChange={e => setModSeller(e.target.value)}>
                <option value="">Tüm satıcılar</option>
                {sellers.map(x => <option key={x.seller_name} value={x.seller_name}>{x.seller_name} ({x.adet})</option>)}
              </select>
              <select style={{ ...s.select, width: 'auto' }} value={modRate} onChange={e => setModRate(Number(e.target.value))}>
                <option value={0}>Tüm puanlar</option>
                {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} yıldız</option>)}
              </select>
              <select style={{ ...s.select, width: 'auto' }} value={modMedya} onChange={e => setModMedya(e.target.value)}>
                <option value="">Fotoğraf farketmez</option>
                <option value="var">Fotoğraflı</option>
                <option value="yok">Fotoğrafsız</option>
              </select>
              <input style={{ ...s.input, maxWidth: '240px' }} placeholder="Ürün adı veya kodu ara"
                value={modUrun} onChange={e => setModUrun(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadModeration()} />
              <input style={{ ...s.input, maxWidth: '200px' }} placeholder="Yorum içinde ara"
                value={modQ} onChange={e => setModQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadModeration()} />
              <button style={s.btnSecondary} onClick={loadModeration} disabled={modLoading}>
                {modLoading ? 'Yükleniyor…' : 'Listele'}
              </button>
              {(modUrun || modQ || modSeller || modRate || modMedya) && (
                <button style={ui.btnQuiet} onClick={() => { setModUrun(''); setModQ(''); setModSeller(''); setModRate(0); setModMedya('') }}>
                  Filtreleri temizle
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', paddingTop: '12px', borderTop: `1px solid ${colors.border}` }}>
              <span style={ui.numMuted}>
                {modToplam} sonuç · {modSecili.length} seçili
              </span>
              <button style={ui.btnSm} onClick={() => setModSecili(modList.map(r => r.review_id))} disabled={!modList.length}>
                Sayfadakileri seç
              </button>
              <button style={ui.btnQuiet} onClick={() => setModSecili([])} disabled={!modSecili.length}>Seçimi temizle</button>
              <div className="sx-wrap" style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button style={s.btnSecondary} onClick={() => yayinDurumu(modSecili, true)} disabled={!modSecili.length || modBusy}>
                  Seçilenleri yayınla
                </button>
                <button style={s.btnSecondary} onClick={() => yayinDurumu(modSecili, false)} disabled={!modSecili.length || modBusy}>
                  Yayından kaldır
                </button>
                <button style={s.btnSecondary} onClick={() => filtreyeUyanlariYayinla(true)} disabled={!modToplam || modBusy}>
                  Filtreye uyan {modToplam} yorumu yayınla
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {modList.map(r => {
              const secili = modSecili.includes(r.review_id)
              return (
                <div key={r.review_id} style={{
                  ...s.cardSm,
                  borderColor: secili ? colors.primary : colors.border,
                  borderWidth: secili ? '2px' : '1px',
                  display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap'
                }}>
                  <input type="checkbox" checked={secili} onChange={() => secimDegistir(r.review_id)}
                    style={{ marginTop: '3px', width: '16px', height: '16px', accentColor: colors.primary, cursor: 'pointer', flexShrink: 0 }} />
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    {(r.urun_adi || r.ty_name) && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap', marginBottom: '6px' }}>
                        <span className="sx-break" style={{ fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>{r.urun_adi || r.ty_name}</span>
                        {r.urun_kodu && <span style={{ fontSize: '11px', color: colors.textMuted, fontFamily: 'monospace' }}>{r.urun_kodu}</span>}
                        <button style={{ ...ui.btnQuiet, padding: '1px 7px', fontSize: '11px' }}
                          title="Bu ürünün yorumlarını filtrele"
                          onClick={() => setModUrun(r.urun_kodu || r.urun_adi || r.ty_name)}>
                          bu üründe filtrele
                        </button>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: colors.warning }}>{'★'.repeat(r.rate)}<span style={{ color: colors.border }}>{'★'.repeat(5 - r.rate)}</span></span>
                      <span style={{ fontSize: '12px', color: colors.textSecondary }}>{r.user_full_name}</span>
                      {r.seller_name && <span style={ui.chip}>{r.seller_name}</span>}
                      {r.trusted && <span style={ui.chip}>Doğrulanmış</span>}
                      {canli(r) && <span style={ui.status(true)}><span style={ui.statusDot(true)} />Yayında</span>}
                      {r.is_hidden && <span style={ui.status(false)}><span style={ui.statusDot(false)} />Yayından kaldırıldı</span>}
                      <span style={{ fontSize: '11px', color: colors.textMuted, marginLeft: 'auto' }}>
                        {r.ty_created_at ? new Date(r.ty_created_at).toLocaleDateString('tr-TR') : ''}
                      </span>
                    </div>
                    <div className="sx-break" style={{ fontSize: '13px', lineHeight: 1.6, color: colors.textPrimary }}>{r.comment}</div>
                    {(r.media || []).length > 0 && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {(r.media || []).slice(0, 6).map((m, i) => (
                          <img key={i} src={m.thumb} alt="" style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: radius.sm, border: `1px solid ${colors.border}` }} />
                        ))}
                      </div>
                    )}
                  </div>
                  <button style={{ ...ui.btnSm, whiteSpace: 'nowrap', flexShrink: 0 }}
                    onClick={() => yayinDurumu([r.review_id], !canli(r))} disabled={modBusy}>
                    {canli(r) ? 'Yayından kaldır' : 'Yayınla'}
                  </button>
                </div>
              )
            })}
            {!modList.length && !modLoading && (
              <div style={{ ...s.cardSm, ...ui.emptyText }}>Bu filtreye uyan yorum yok.</div>
            )}
          </div>
        </>
      )}

      {matchFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' }}
          onClick={e => { if (e.target === e.currentTarget) setMatchFor(null) }}>
          <div style={{ background: colors.bgCard, borderRadius: radius.lg, padding: '24px', width: 'min(560px, calc(100vw - 24px))', maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <h3 style={{ ...s.sectionTitle, marginBottom: '4px' }}>Trendyol ürünü bağla</h3>
            <p className="sx-break" style={{ ...s.sectionSubtitle, marginBottom: '16px' }}>{matchFor.name}</p>
            <Row label="Trendyol ürün linki veya contentId"
              hint="Örnek: https://www.trendyol.com/mamajoo/…-p-165258968 — sadece numara da yazabilirsiniz.">
              <input style={s.input} value={matchInput} onChange={e => setMatchInput(e.target.value)}
                placeholder="https://www.trendyol.com/…-p-165258968" />
            </Row>
            {matchErr && <div style={{ ...s.alertDanger, marginBottom: '14px' }}>{matchErr}</div>}
            {preview && (
              <div style={{ ...s.cardFlat, marginBottom: '16px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                {preview.image && <img src={preview.image} alt="" style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: radius.md, flexShrink: 0 }} />}
                <div className="sx-break" style={{ fontSize: '13px', flex: '1 1 180px', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: '3px' }}>{preview.name}</div>
                  <div style={{ color: colors.textSecondary, fontSize: '12px' }}>
                    {preview.brand} · {preview.category} · kod: {preview.product_code || '—'}
                  </div>
                  <div style={{ color: colors.textSecondary, fontSize: '12px', marginTop: '3px' }}>
                    Satıcı: {preview.merchant_name || '—'} · {preview.comment_count} yorum · puan {Number(preview.avg_rating).toFixed(1)}
                  </div>
                </div>
              </div>
            )}
            <div className="sx-wrap" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button style={ui.btnQuiet} onClick={() => setMatchFor(null)}>Vazgeç</button>
              {!preview
                ? <button style={s.btnPrimary} onClick={doPreview} disabled={matchBusy || !matchInput.trim()}>
                    {matchBusy ? 'Kontrol ediliyor…' : 'Önizle'}
                  </button>
                : <button style={s.btnPrimary} onClick={confirmMatch} disabled={matchBusy}>
                    {matchBusy ? 'Bağlanıyor…' : 'Bu ürünü bağla'}
                  </button>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
